
import { Vue, Component, Prop } from 'vue-property-decorator'
import * as T from '@/types'
import S from '@/store'
import config from '@/config'

import moment from 'moment'
import { formatAbsoluteDate } from '@/utils/utils-datetime'

@Component
export default class UiDownloadButton extends Vue {

    ////////////////////////////////////////////////////////////////
    // API
    ////////////////////////////////////////////////////////////////
    
    // content
    @Prop({ type: Object, required: true }) file!: T.File
    @Prop({ type: Boolean, default: true }) showLabel!: boolean
    @Prop({ type: String, default: null }) tooltip!: string | null

    // size
    @Prop({ type: Boolean, default: false }) tiny!: boolean
    @Prop({ type: Boolean, default: true }) small!: boolean
    @Prop({ type: Boolean, default: false }) big!: boolean

    ////////////////////////////////////////////////////////////////
    // computed » state
    ////////////////////////////////////////////////////////////////
    
    get downloading () {
        return this.$store.getters['files/downloading'].includes(this.file.id)
    }

    get failed () {
        return this.$store.getters['files/failed'].includes(this.file.id)
    }

    get successful () {
        return this.$store.getters['files/successful'].includes(this.file.id)
    }

    get broken () {
        return this.$store.getters['files/brokenLinks'].includes(this.file.id)
    }
    
    get downloadSuccessful () {
        return !!(this.successful || this.file.downloadedAt)
    }

    get toBeDownloaded () {
        return !(this.downloading || this.failed || this.broken || this.downloadSuccessful)
    }

    ////////////////////////////////////////////////////////////////
    // computed
    ////////////////////////////////////////////////////////////////

    get buttonLabel () {
        if (this.failed) return 'Retry'
        if (this.broken) return 'Failed'
        return 'Download'
    }

    get buttonIcon () {
        if (this.failed) return 'restart-alert'
        if (this.broken) return 'alert-circle'
        if (this.downloadSuccessful) return 'check'
        return 'download'
    }

    get buttonTooltip () {
        if (this.tooltip) return this.tooltip
        if (this.broken) return `We were unable to retrieve this file. Please contact support at ${this.supportEmail} for assistance.`
        if (this.downloadSuccessful) return this.file.downloadedAt ? `This file was downloaded on ${formatAbsoluteDate(this.file.downloadedAt)}` : `This file was downloaded recently.`
        return undefined
    }

    get buttonProps () {
        return {
            // Failed - to be retried
            danger: this.failed,

            // Broken - can't be retried
            readOnly: this.broken,
            subtle: !this.broken,

            // Download successful
            success: this.downloadSuccessful && !this.failed && !this.broken,

            // To be downloaded
            primary: this.toBeDownloaded,
            badge: this.toBeDownloaded,
            externalBadge: this.toBeDownloaded,

            // Size
            big: this.big,
            small: this.small && !this.big,
        }
    }

    get supportEmail () {
        return config.SUPPORT.SUPPORT_EMAIL_ADDRESS
    }

}
